import { useState, useEffect } from "react";
import { useAssessments } from "@/hooks/useAssessments";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, XCircle, Trophy, Shield, Target, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import CheckpointDebriefPanel from "@/components/ai/CheckpointDebriefPanel";
import type { AnyValue } from "@/lib/rowTypes";

type AssessmentAnswer = Record<string, AnyValue>;

interface Props {
  assessmentId: string;
  onComplete: (passed: boolean) => void;
  onBack: () => void;
}

interface ShuffledQ {
  id: string;
  question_text: string;
  options: { key: string; text: string; originalKey: string }[];
  correctKey: string; // populated by server grade
  explanation: string | null;
  skill_category: string | null;
}

export default function AssessmentScreen({ assessmentId, onComplete, onBack }: Props) {
  const { getAssessmentQuestions, submitAttempt } = useAssessments();
  const [assessment, setAssessment] = useState<AnyValue>(null);
  const [questions, setQuestions] = useState<ShuffledQ[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [answers, setAnswers] = useState<AssessmentAnswer[]>([]);
  const [phase, setPhase] = useState<"loading" | "intro" | "quiz" | "results">("loading");
  const [skillResults, setSkillResults] = useState<Record<string, { correct: number; total: number }>>({});

  useEffect(() => {
    const load = async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: a } = await (supabase.from("assessments").select("*").eq("id", assessmentId).single());
      setAssessment(a);
      const qs = await getAssessmentQuestions(assessmentId);
      const shuffled: ShuffledQ[] = qs.map((q: AnyValue) => {
        const entries = [
          { originalKey: "A", text: q.option_a },
          { originalKey: "B", text: q.option_b },
          { originalKey: "C", text: q.option_c },
          { originalKey: "D", text: q.option_d },
        ];
        for (let i = entries.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [entries[i], entries[j]] = [entries[j], entries[i]];
        }
        const labels = ["A", "B", "C", "D"];
        return {
          id: q.id,
          question_text: q.question_text,
          options: entries.map((e, i) => ({ key: labels[i], text: e.text, originalKey: e.originalKey })),
          correctKey: "",
          explanation: q.explanation ?? null,
          skill_category: q.skill_category,
        };
      });
      setQuestions(shuffled);
      setPhase("intro");
    };
    void load();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [assessmentId]);

  const handleAnswer = async (key: string) => {
    if (showExplanation) return;
    setSelectedAnswer(key);
    const q = questions[currentIndex];
    const selectedOriginal = q.options.find(o => o.key === key)?.originalKey ?? key;
    const { supabase } = await import("@/integrations/supabase/client");
    const { data: gradeData, error: gradeError } = await supabase.rpc(
      "grade_assessment_question",
      { _question_id: q.id, _selected: selectedOriginal }
    );
    if (gradeError || !gradeData) {
      toast.error("Couldn't grade your answer. Please try again.");
      setSelectedAnswer(null);
      return;
    }
    const grade = gradeData as { is_correct: boolean; correct_answer: string; explanation: string | null };
    const correctDisplayKey = q.options.find(o => o.originalKey === grade.correct_answer)?.key ?? "";
    setQuestions(prev => prev.map((qq, i) => i === currentIndex
      ? { ...qq, correctKey: correctDisplayKey, explanation: grade.explanation ?? qq.explanation }
      : qq));
    const isCorrect = grade.is_correct;
    setShowExplanation(true);
    if (isCorrect) setCorrectCount(c => c + 1);
    setAnswers(prev => [...prev, { question_id: q.id, selected: key, correct: correctDisplayKey, is_correct: isCorrect }]);
    if (q.skill_category) {
      setSkillResults(prev => {
        const cat = q.skill_category!;
        const existing = prev[cat] || { correct: 0, total: 0 };
        return { ...prev, [cat]: { correct: existing.correct + (isCorrect ? 1 : 0), total: existing.total + 1 } };
      });
    }
  };

  const handleNext = async () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(i => i + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      await submitAttempt(assessmentId, correctCount, questions.length, answers);
      setPhase("results");
    }
  };

  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const passed = questions.length > 0 && (correctCount / questions.length) * 100 >= (assessment?.recommended_pass_threshold || 80);
  const isCapstone = assessment?.is_capstone;
  const isCross = assessment?.is_cross_course_mastery_check;
  const typeLabel = isCapstone ? "Capstone Assessment" : isCross ? "Cross-Course Mastery Check" : "Midpoint Checkpoint";
  const typeIcon = isCapstone ? Trophy : isCross ? Shield : Target;
  const TypeIcon = typeIcon;

  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-6 text-center">
          <div className={cn(
            "mx-auto flex h-16 w-16 items-center justify-center rounded-2xl",
            isCapstone ? "bg-accent/15 shadow-[0_0_30px_hsl(var(--accent)/0.2)]" : "bg-primary/15 shadow-[0_0_30px_hsl(var(--primary)/0.2)]"
          )}>
            <TypeIcon className={cn("h-8 w-8", isCapstone ? "text-accent" : "text-primary")} />
          </div>
          <div>
            <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-1">{typeLabel}</p>
            <h1 className="font-heading text-xl font-bold text-foreground">{assessment?.title}</h1>
          </div>
          <p className="font-body text-sm leading-relaxed text-muted-foreground">{assessment?.description}</p>
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <span>{questions.length} questions</span>
            <span>•</span>
            <span>{assessment?.recommended_pass_threshold || 80}% to pass</span>
          </div>
          {assessment?.mastery_badge_award && (
            <p className="text-xs text-primary font-heading">🏅 Pass to earn: <span className="font-bold">{assessment.mastery_badge_award}</span></p>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onBack} className="flex-1">Back</Button>
            <Button onClick={() => setPhase("quiz")} className={cn(
              "flex-1 gap-2",
              isCapstone ? "bg-accent/15 border border-accent/30 text-accent hover:bg-accent/25" : "bg-primary/15 border border-primary/30 text-primary hover:bg-primary/25"
            )}>
              Begin <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "results") {
    const pct = questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0;
    const skillCategories = assessment?.skill_categories ? JSON.parse(typeof assessment.skill_categories === "string" ? assessment.skill_categories : JSON.stringify(assessment.skill_categories)) : [];

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-6 text-center">
          <div className={cn(
            "mx-auto flex h-20 w-20 items-center justify-center rounded-2xl",
            passed ? "bg-emerald-500/15 shadow-[0_0_40px_rgba(16,185,129,0.2)]" : "bg-destructive/15"
          )}>
            {passed ? <Trophy className="h-10 w-10 text-emerald-400" /> : <Target className="h-10 w-10 text-destructive" />}
          </div>
          <div>
            <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground">{typeLabel}</p>
            <h1 className="font-heading text-2xl font-bold text-foreground mt-1">
              {passed ? "Assessment Passed" : "Not Yet — Keep Going"}
            </h1>
          </div>
          <div className={cn(
            "text-4xl font-heading font-bold",
            passed ? "text-emerald-400" : "text-destructive"
          )}>
            {pct}%
          </div>
          <p className="text-sm text-muted-foreground">{correctCount}/{questions.length} correct</p>

          {passed && assessment?.mastery_badge_award && (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
              <p className="text-xs font-heading text-primary">🏅 Badge Earned: <span className="font-bold">{assessment.mastery_badge_award}</span></p>
            </div>
          )}

          {/* Skill Breakdown */}
          {skillCategories.length > 0 && (
            <div className="space-y-2 text-left">
              <p className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Skill Breakdown</p>
              {skillCategories.map((cat: AnyValue) => {
                const result = skillResults[cat.key];
                const catPct = result ? Math.round((result.correct / result.total) * 100) : 0;
                const isStrong = catPct >= 80;
                return (
                  <div key={cat.key} className="flex items-center gap-2 rounded-lg border border-border/50 bg-card/40 p-2.5">
                    {isStrong ? <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" /> : <XCircle className="h-4 w-4 text-amber-400 shrink-0" />}
                    <span className="text-xs font-body text-foreground flex-1">{cat.label}</span>
                    <span className={cn("text-xs font-heading font-bold", isStrong ? "text-emerald-400" : "text-amber-400")}>
                      {result ? `${result.correct}/${result.total}` : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <CheckpointDebriefPanel
            assessmentTitle={assessment?.title || "Assessment"}
            assessmentType={assessment?.assessment_type || "checkpoint"}
            score={correctCount}
            maxScore={questions.length}
            passed={passed}
            strengths={Object.entries(skillResults).filter(([_, v]) => v.total > 0 && v.correct / v.total >= 0.8).map(([k]) => k)}
            weaknesses={Object.entries(skillResults).filter(([_, v]) => v.total > 0 && v.correct / v.total < 0.8).map(([k]) => k)}
          />

          <Button onClick={() => onComplete(passed)} className="w-full gap-2">
            {passed ? "Continue" : "Return to Course"} <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Quiz phase
  const q = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-primary/10">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-heading text-muted-foreground">{typeLabel}</p>
            <p className="text-xs font-heading font-bold text-foreground">{currentIndex + 1}/{questions.length}</p>
          </div>
          <div className="h-1.5 rounded-full bg-muted/30 overflow-hidden">
            <div className={cn("h-full rounded-full transition-all duration-500", isCapstone ? "bg-accent" : "bg-primary")} style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <h2 className="font-heading text-base font-bold text-foreground leading-relaxed">{q.question_text}</h2>

        <div className="space-y-3">
          {q.options.map(opt => {
            const isSelected = selectedAnswer === opt.key;
            const isCorrect = opt.key === q.correctKey;
            let style = "border-border/60 bg-card/60 hover:border-primary/30";
            if (showExplanation) {
              if (isCorrect) style = "border-emerald-500/50 bg-emerald-500/10";
              else if (isSelected) style = "border-destructive/50 bg-destructive/10";
              else style = "border-border/30 bg-card/30 opacity-60";
            }

            return (
              <button
                key={opt.key}
                onClick={() => handleAnswer(opt.key)}
                disabled={showExplanation}
                className={cn("w-full text-left rounded-xl border p-4 transition-all", style)}
              >
                <div className="flex items-start gap-3">
                  <span className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-heading font-bold",
                    showExplanation && isCorrect ? "border-emerald-500 text-emerald-400" :
                    showExplanation && isSelected ? "border-destructive text-destructive" :
                    "border-border text-muted-foreground"
                  )}>
                    {opt.key}
                  </span>
                  <p className="font-body text-sm text-foreground/90 leading-relaxed">{opt.text}</p>
                </div>
              </button>
            );
          })}
        </div>

        {showExplanation && q.explanation && (
          <div className={cn(
            "rounded-xl border p-4",
            selectedAnswer === q.correctKey ? "border-emerald-500/20 bg-emerald-500/5" : "border-amber-500/20 bg-amber-500/5"
          )}>
            <p className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider mb-1">
              {selectedAnswer === q.correctKey ? "✓ Correct" : "✗ Incorrect"}
            </p>
            <p className="font-body text-sm leading-relaxed text-foreground/80">{q.explanation}</p>
          </div>
        )}

        {showExplanation && (
          <Button onClick={handleNext} className={cn(
            "w-full gap-2",
            isCapstone ? "bg-accent/15 border border-accent/30 text-accent hover:bg-accent/25" : "bg-primary/15 border border-primary/30 text-primary hover:bg-primary/25"
          )}>
            {currentIndex < questions.length - 1 ? "Next Question" : "See Results"}
            <ArrowRight className="w-4 h-4" />
          </Button>
        )}
      </main>
    </div>
  );
}
